<script setup lang="ts">

// The home page is prerendered (static), so a build-time query would freeze and
// miss posts that auto-publish later. Fetch from a runtime endpoint on the client
// instead — it returns just these few cards, never the whole collection.
const { data: posts } = await useFetch('/api/latest-posts', { server: false })

function formatDate(date: string) {
  return new Date(date).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}
</script>

<template>
  <section v-if="posts?.length" id="blog" class="py-20 md:py-40 relative">
    <div class="max-w-7xl mx-auto px-6 md:px-12">
      <div class="flex flex-col gap-6 md:flex-row md:items-end md:justify-between mb-16" data-aos="fade">
        <div>
          <div class="flex items-center gap-3 mb-6">
            <span class="text-xs tracking-[0.25em] text-blu-900 dark:text-blu-400 uppercase font-medium">From the blog</span>
          </div>
          <h2 class="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-700 dark:text-white">
            Latest writing
          </h2>
        </div>
        <NuxtLink to="/blog" class="font-medium text-blu-600 underline decoration-dotted underline-offset-4 transition-colors hover:decoration-solid dark:text-blu-400 whitespace-nowrap">View all posts</NuxtLink>
      </div>

      <!-- The posts in three columns, divided only by hairlines. -->
      <div class="grid divide-y divide-slate-900/10 dark:divide-white/10 md:-mx-8 md:grid-cols-3 md:divide-x md:divide-y-0">
        <NuxtLink
          v-for="(post, index) in posts"
          :key="post.path"
          :to="post.path"
          data-aos="fade"
          :data-aos-delay="100 * index"
          class="group block py-8 first:pt-0 last:pb-0 md:px-8 md:py-0"
        >
          <div class="mb-3 flex items-center gap-3 text-xs">
            <span class="font-medium text-blu-600 dark:text-blu-400">{{ post.category }}</span>
            <time :datetime="post.date" class="text-slate-500 dark:text-white/50">{{ formatDate(post.date) }}</time>
          </div>
          <h3 class="mb-3 text-xl font-semibold leading-snug text-slate-700 transition-colors group-hover:text-blu-600 dark:text-white dark:group-hover:text-blu-400">
            {{ post.title }}
          </h3>
          <p class="text-sm leading-relaxed text-slate-700/75 dark:text-white/50 line-clamp-3">{{ post.description }}</p>
        </NuxtLink>
      </div>
    </div>
  </section>
</template>
